import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  User, Home, TrendingUp, ShoppingCart, KeyRound, Wrench, LogOut, Menu, X, Users, ChevronDown, Search, Network, FolderOpen, Package, ShoppingBag, Receipt, FileText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PortalProvider, usePortal } from "@/context/PortalContext";

const LINKS = [
  { to: "/portal/profile", label: "My Profile", icon: User, testid: "portal-sidebar-my-profile" },
  { to: "/portal/properties", label: "Properties", icon: Home, testid: "portal-sidebar-my-properties" },
  { to: "/portal/sale", label: "Sale", icon: TrendingUp, testid: "portal-sidebar-sale" },
  { to: "/portal/purchase", label: "Purchase", icon: ShoppingCart, testid: "portal-sidebar-purchase" },
  { to: "/portal/tenancy", label: "Tenancy", icon: KeyRound, testid: "portal-sidebar-tenancy" },
  { to: "/portal/maintenance", label: "Maintenance", icon: Wrench, testid: "portal-sidebar-maintenance" },
];
const SCOPED = ["legal_rep", "accountant", "broker", "caretaker"];
const ADMIN_LINKS = [
  { to: "/portal/users", label: "User Management", icon: Users, testid: "portal-sidebar-users" },
  { to: "/portal/allocations", label: "Allocations", icon: Network, testid: "portal-sidebar-allocations" },
  { to: "/portal/product-setup", label: "Product Setup", icon: Package, testid: "portal-sidebar-product-setup" },
  { to: "/portal/invoices", label: "Invoices", icon: FileText, testid: "portal-sidebar-invoices" },
  { to: "/portal/contractor-invoices", label: "Contractor Invoices", icon: FileText, testid: "portal-sidebar-contractor-invoices" },
];

function ClientSelector() {
  const { clients, selectedClientId, setSelectedClientId } = usePortal();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = clients.find((c) => c.id === selectedClientId);
  const filtered = clients.filter((c) => `${c.name} ${c.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="relative" data-testid="client-selector">
      <button onClick={() => setOpen((o) => !o)} data-testid="client-selector-trigger"
        className="inline-flex items-center gap-2 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 hover:border-emerald-400 min-w-[210px] justify-between transition-colors">
        <span className="flex items-center gap-2 truncate"><Users className="h-4 w-4 text-emerald-600 shrink-0" />{selected ? selected.name : "All clients"}</span>
        <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-72 z-50 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden" data-testid="client-selector-panel">
            <div className="p-2 border-b border-slate-100 flex items-center gap-2">
              <Search className="h-4 w-4 text-slate-400 shrink-0" />
              <input autoFocus data-testid="client-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search clients…" className="w-full text-sm py-1 bg-transparent focus:outline-none text-slate-900" />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              <button data-testid="client-option-all" onClick={() => { setSelectedClientId(""); setOpen(false); setQ(""); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${!selectedClientId ? "text-emerald-700 font-medium" : "text-slate-700"}`}>All clients</button>
              {filtered.map((c) => (
                <button key={c.id} data-testid="client-option" onClick={() => { setSelectedClientId(c.id); setOpen(false); setQ(""); }}
                  className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${selectedClientId === c.id ? "text-emerald-700" : "text-slate-700"}`}>
                  <span className="block text-sm truncate">{c.name}</span>
                  <span className="block text-xs text-slate-400 truncate">{c.email}</span>
                </button>
              ))}
              {filtered.length === 0 && <div className="px-3 py-3 text-sm text-slate-400">No clients found</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PortalHeaderBar() {
  const { canSelectClient } = usePortal();
  if (!canSelectClient) return null;
  return (
    <div className="sticky top-14 lg:top-0 z-30 bg-slate-50/85 backdrop-blur border-b border-slate-200 px-6 md:px-10 py-3 flex items-center justify-end gap-3">
      <span className="text-xs text-slate-500 font-mono hidden sm:block">Viewing client</span>
      <ClientSelector />
    </div>
  );
}

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/login"); };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all duration-200 ${
      isActive ? "bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium"
               : "text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-transparent"}`;

  const PIPE = { "/portal/sale": "sale", "/portal/purchase": "purchase", "/portal/tenancy": "tenancy", "/portal/maintenance": "maintenance" };
  const SidebarContent = () => {
    const { selectedClientId } = usePortal();
    const cp = user?.client_pipelines || {};
    const pipeAllowed = (pk) => {
      if (!SCOPED.includes(user?.role)) return true;
      if (user?.role === "caretaker" && pk !== "maintenance") return false;
      if (["legal_rep", "accountant"].includes(user?.role) && pk === "maintenance") return false;
      if (selectedClientId) return (cp[selectedClientId] || []).includes(pk);
      return Object.values(cp).some((arr) => (arr || []).includes(pk));
    };
    const visibleLinks = LINKS.filter((l) => { const pk = PIPE[l.to]; return pk ? pipeAllowed(pk) : true; });
    return (
    <>
      <div className="flex items-center gap-2.5 px-2 mb-8">
        <img src="/va-logo-orange.png" alt="VA Property Care" className="h-9 w-9 object-contain" />
        <span className="font-display font-bold text-slate-900">VA Property Care</span>
      </div>
      <nav className="space-y-1.5 flex-1">
        {visibleLinks.map((l) => (
          <NavLink key={l.to} to={l.to} data-testid={l.testid} className={linkClass} onClick={() => setOpen(false)}>
            <l.icon className="h-4.5 w-4.5" /> {l.label}
          </NavLink>
        ))}
        {["admin", "power_user", "legal_rep", "accountant"].includes(user?.role) && (
          <NavLink to="/portal/client-profiles" data-testid="portal-sidebar-client-profiles" className={linkClass} onClick={() => setOpen(false)}>
            <FolderOpen className="h-4.5 w-4.5" /> Client Files
          </NavLink>
        )}
        {SCOPED.includes(user?.role) && (
          <NavLink to="/portal/contractor-invoice" data-testid="portal-sidebar-raise-invoice" className={linkClass} onClick={() => setOpen(false)}>
            <FileText className="h-4.5 w-4.5" /> Raise Invoice
          </NavLink>
        )}
        {user?.role === "client" && (
          <>
            <NavLink to="/portal/products" data-testid="portal-sidebar-products" className={linkClass} onClick={() => setOpen(false)}>
              <ShoppingBag className="h-4.5 w-4.5" /> Products
            </NavLink>
            <NavLink to="/portal/orders" data-testid="portal-sidebar-orders" className={linkClass} onClick={() => setOpen(false)}>
              <Receipt className="h-4.5 w-4.5" /> Order History
            </NavLink>
            <NavLink to="/portal/my-invoices" data-testid="portal-sidebar-my-invoices" className={linkClass} onClick={() => setOpen(false)}>
              <FileText className="h-4.5 w-4.5" /> My Invoices
            </NavLink>
          </>
        )}
        {user?.role === "admin" && (
          <>
            <div className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-[0.2em] font-mono text-slate-400">Admin</div>
            {ADMIN_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={l.testid} className={linkClass} onClick={() => setOpen(false)}>
                <l.icon className="h-4.5 w-4.5" /> {l.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>
      <button onClick={doLogout} data-testid="portal-sidebar-logout"
        className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-rose-600 hover:bg-rose-50 transition-colors w-full mt-4">
        <LogOut className="h-4.5 w-4.5" /> Logout
      </button>
    </>
    );
  };

  return (
    <PortalProvider>
    <div className="min-h-screen bg-slate-50 text-slate-900 flex">
      <aside className="hidden lg:flex w-64 flex-shrink-0 flex-col p-4 border-r border-slate-200 bg-white sticky top-0 h-screen overflow-y-auto">
        <SidebarContent />
      </aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-white/90 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src="/va-logo-orange.png" alt="VA Property Care" className="h-8 w-8 object-contain" />
          <span className="font-display font-bold text-sm text-slate-900">VA Property Care</span>
        </div>
        <button onClick={() => setOpen(!open)} data-testid="portal-mobile-toggle" className="text-slate-700">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-30 bg-slate-900/30" onClick={() => setOpen(false)}>
          <aside className="absolute left-0 top-0 h-full w-72 bg-white border-r border-slate-200 p-4 flex flex-col pt-16 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <SidebarContent />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <PortalHeaderBar />
        <div className="p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
    </PortalProvider>
  );
}
